import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { API, Empty } from "./shared";

// ============================================================
// VIEW: BUYER SAMPLE PACKS
// Flat-fee sample kits curated from up to 5 verified printers. Fee locked
// via Razorpay before the pack moves to curating. Buyer can rate the pack
// post-delivery; feedback goes into the supplier Quality Score.
// ============================================================
const SP_COLS = "1.3fr 1.1fr .9fr 1.1fr 110px";

export function BuyerSamplePacks({ user }) {
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showRate, setShowRate] = useState(false);
  const [paying, setPaying] = useState(null); // sp id currently being paid

  const load = async () => {
    try {
      const [c, mine] = await Promise.all([
        axios.get(`${API}/sample-packs/config`).then(r => r.data),
        axios.get(`${API}/sample-packs/mine`).then(r => r.data),
      ]);
      setCfg(c);
      setItems(mine.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sample packs");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const payQuote = async (sp) => {
    setPaying(sp.id);
    try {
      const rzpCfg = await axios.get(`${API}/razorpay/config`).then(r => r.data);
      // Ensure we have a Razorpay order id. Legacy quotes written before the
      // admin-side order-create fix don't carry one; the new /payment-order
      // endpoint lazy-creates or returns the existing one so the buyer isn't
      // stranded on a quote they can't pay for.
      let order;
      if (sp.razorpay_order_id) {
        order = { id: sp.razorpay_order_id, amount: sp.quoted_amount_inr * 100, currency: "INR" };
      } else {
        order = await axios.post(`${API}/sample-packs/${sp.id}/payment-order`).then(r => r.data);
      }
      const resp = await openRazorpayCheckout({
        keyId: rzpCfg.key_id,
        order,
        name: "GoPrintPack · Sample Pack",
        description: `${sp.product_category} sample`,
        prefill: { name: sp.ship_name, email: user?.email || "", contact: sp.ship_phone },
        notes: { sample_pack_id: sp.id, flow: "sample_pack" },
      });
      await axios.post(`${API}/sample-packs/${sp.id}/confirm-payment`, resp);
      toast.success(`Payment of ₹${sp.quoted_amount_inr.toLocaleString("en-IN")} confirmed — your samples are being prepared.`);
      await load();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") toast.info("Payment cancelled. You can pay anytime from this card.");
      else toast.error(e?.description || e?.response?.data?.detail || "Could not complete payment.");
    } finally {
      setPaying(null);
    }
  };

  const openRate = (sp) => { setSelected(sp); setShowRate(true); };

  if (!items) return <section className="content"><Empty text="Loading your sample packs…"/></section>;

  const statusMeta = {
    pending_quote: { bg: "#FEF3C7", fg: "#92400E", label: "⏳ Awaiting quote" },
    quoted:        { bg: "#DBEAFE", fg: "#1E40AF", label: "💰 Quote ready · pay to confirm" },
    paid:          { bg: "#DCFCE7", fg: "#065F46", label: "✅ Paid · preparing" },
    curating:      { bg: "#E0E7FF", fg: "#3730A3", label: "🎨 Curating" },
    shipped:       { bg: "#E0E7FF", fg: "#3730A3", label: "🚚 Shipped" },
    delivered:     { bg: "#DCFCE7", fg: "#065F46", label: "📬 Delivered" },
    rated:         { bg: "#F3E8FF", fg: "#6B21A8", label: "⭐ Rated" },
    cancelled:     { bg: "#FEE2E2", fg: "#991B1B", label: "Cancelled" },
  };

  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">GOPRINTPACK™ · VERIFIED SAMPLE PACKS</p>
          <h2>Hold the finish in your hand before you commit</h2>
          <p className="subhead">Tell us what you want to sample — substrates, finishes, use-case. Our team posts a tailored quote within 24 hours. Pay only after you've seen the price. Ships right after payment.</p>
        </div>
        <button data-testid="sample-pack-new-button" className="primary-btn" onClick={() => setShowForm(true)}>
          <Plus size={16}/> Request a sample pack
        </button>
      </div>

      {items.length === 0 ? (
        <Empty text="No sample packs yet. Click above — tell us what to sample and we'll quote within 24 hours."/>
      ) : (
        <div className="job-table" data-testid="sample-pack-list">
          <div className="table-head" style={{gridTemplateColumns: SP_COLS}}>
            <span>REQUEST</span><span>SPECIFICATION</span><span>STATUS</span><span>ACTIVITY</span><span></span>
          </div>
          {items.map(sp => {
            const sm = statusMeta[sp.status] || { bg: "#F3F4F6", fg: "#374151", label: sp.status };
            const canPay = sp.status === "quoted" && sp.quoted_amount_inr;
            const quoteIncomplete = sp.status === "quoted" && !sp.quoted_amount_inr;
            const badge = quoteIncomplete ? { bg: "#FEF3C7", fg: "#92400E", label: "⏳ Finalising your quote" } : sm;
            const specs = [...(sp.substrate_preferences || []), ...(sp.finish_preferences || [])];
            return (
              <div key={sp.id} data-testid={`sample-pack-${sp.id}`} className="job-row table-row" style={{gridTemplateColumns: SP_COLS, cursor:"default"}}>
                <div className="job-title">
                  <div className="job-symbol">SP</div>
                  <div><b>{sp.product_category}</b><span>#{sp.id.slice(-6)} · {sp.created_at ? new Date(sp.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</span></div>
                </div>
                <div className="spec">
                  <b>{specs.slice(0, 3).join(" · ") || "No preference"}{specs.length > 3 ? ` +${specs.length - 3}` : ""}</b>
                  <span>{sp.estimated_delivery_days != null ? `${sp.estimated_delivery_days}-day delivery` : "Delivery time on quote"}</span>
                </div>
                <div>
                  <span data-testid={`sample-pack-status-${sp.id}`} style={{background: badge.bg, color: badge.fg, padding:"3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", display:"inline-block"}}>{badge.label}</span>
                </div>
                <div className="activity" data-testid={`sample-pack-activity-${sp.id}`}>
                  {sp.quoted_amount_inr != null ? (
                    <b>₹{sp.quoted_amount_inr.toLocaleString("en-IN")} {sp.payment_status === "paid" ? "· paid" : "· awaiting payment"}</b>
                  ) : <b>Quote within 24 hours</b>}
                  <span>
                    {sp.courier && sp.tracking_number ? `${sp.courier} · ${sp.tracking_number}` : quoteIncomplete ? "Our team is costing this pack" : sp.admin_quote_note ? `Note: ${sp.admin_quote_note}` : "—"}
                  </span>
                </div>
                <div style={{justifySelf:"end"}}>
                  {canPay ? (
                    <button data-testid={`sample-pack-pay-${sp.id}`} onClick={() => payQuote(sp)} disabled={paying === sp.id}
                      style={{background:"#111827",color:"#FACC15",border:"none",padding:"6px 12px",borderRadius:8,fontSize:11,fontWeight:800,cursor: paying === sp.id ? "wait" : "pointer",whiteSpace:"nowrap"}}>
                      {paying === sp.id ? "Opening…" : `Pay ₹${sp.quoted_amount_inr.toLocaleString("en-IN")}`}
                    </button>
                  ) : (sp.status === "shipped" || sp.status === "delivered") ? (
                    !sp.rating ? (
                      <button data-testid={`sample-pack-rate-${sp.id}`} onClick={() => openRate(sp)} style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A",padding:"6px 12px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>Rate pack ★</button>
                    ) : (
                      <span style={{display:"flex",gap:2}}>{[1,2,3,4,5].map(s => <span key={s} style={{color: s <= sp.rating.stars ? "#F59E0B" : "#E5E7EB", fontSize:14, lineHeight:1}}>★</span>)}</span>
                    )
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <SamplePackRequestModal
          user={user}
          onClose={() => setShowForm(false)}
          onDone={async () => { setShowForm(false); await load(); }}
        />
      )}
      {showRate && selected && (
        <SamplePackRatingModal
          samplePack={selected}
          onClose={() => { setShowRate(false); setSelected(null); }}
          onDone={async () => { setShowRate(false); setSelected(null); await load(); }}
        />
      )}
    </section>
  );
}


export function SamplePackRequestModal({ user, onClose, onDone }) {
  const [cat, setCat] = useState("");
  const [subs, setSubs] = useState([]);
  const [fins, setFins] = useState([]);
  const [intendedUse, setIntendedUse] = useState("");
  const [sampleQty, setSampleQty] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [notes, setNotes] = useState("");
  const [ship, setShip] = useState({
    ship_name: user?.name || "",
    ship_phone: "",
    ship_address_line1: "",
    ship_address_line2: "",
    ship_city: "",
    ship_state: "",
    ship_postal_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const SUBSTRATES = ["Kraft board", "SBS board", "Corrugated", "Rigid board", "Metallic foil", "PET laminate", "Rice paper", "Textured specialty"];
  const FINISHES = ["Gloss UV", "Matte lamination", "Spot UV", "Embossing", "Foil stamp", "Soft-touch", "Debossing", "Edge paint"];

  const toggleChip = (list, setList, val) => {
    setList(list.includes(val) ? list.filter(x => x !== val) : [...list, val]);
  };

  const submit = async () => {
    setErr("");
    if (!cat.trim()) return setErr("Pick a product category.");
    if (!ship.ship_name || !ship.ship_phone || !ship.ship_address_line1 || !ship.ship_city || !ship.ship_state || !ship.ship_postal_code) {
      return setErr("Fill the full shipping address so we can courier your kit.");
    }
    setBusy(true);
    try {
      const payload = {
        product_category: cat.trim(),
        substrate_preferences: subs,
        finish_preferences: fins,
        intended_use: intendedUse.trim() || undefined,
        sample_quantity: sampleQty ? Number(sampleQty) : undefined,
        needed_by: neededBy.trim() || undefined,
        notes: notes.trim(),
        ...ship,
      };
      await axios.post(`${API}/sample-packs`, payload);
      toast.success("Request submitted — we'll post a tailored quote within 24 hours.");
      onDone && onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create sample pack request.");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="sample-pack-form" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:720,maxWidth:"96vw",padding:"24px 28px",maxHeight:"92vh",overflow:"auto"}}>
        <h3 style={{margin:"0 0 6px",fontFamily:"'Fraunces', serif",fontSize:22}}>Request a sample pack</h3>
        <p style={{margin:"0 0 16px",fontSize:12,color:"#6B7280"}}>Tell us what you want to sample. Our team posts a quote within <b>24 hours</b>. You pay only after seeing the price — no commitment up front.</p>

        {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Product category *</label>
        <input data-testid="sp-category" value={cat} onChange={e => setCat(e.target.value)} placeholder="e.g. Mono carton for whisky, Flexible pouch for granola" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px"}}/>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Intended use (helps us pick the right finishes)</label>
        <input data-testid="sp-intended-use" value={intendedUse} onChange={e => setIntendedUse(e.target.value)} placeholder="e.g. Premium gifting SKU for Diwali campaign, chilled e-commerce shipper" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px"}}/>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Number of sample variants</label>
            <input data-testid="sp-qty" type="number" min={1} max={20} value={sampleQty} onChange={e => setSampleQty(e.target.value)} placeholder="e.g. 4" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,marginTop:6}}/>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Needed by (optional)</label>
            <input data-testid="sp-needed-by" value={neededBy} onChange={e => setNeededBy(e.target.value)} placeholder="e.g. 15 Oct, or ASAP" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,marginTop:6}}/>
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Substrate preferences</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
            {SUBSTRATES.map(s => (
              <button key={s} type="button" data-testid={`sp-substrate-${s.replace(/\s+/g,'-').toLowerCase()}`} onClick={() => toggleChip(subs, setSubs, s)} style={{background: subs.includes(s) ? "#111827" : "#F3F4F6", color: subs.includes(s) ? "#FACC15" : "#374151", border:"none",padding:"6px 12px",borderRadius:999,fontSize:11,fontWeight:600,cursor:"pointer"}}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Finish preferences</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
            {FINISHES.map(f => (
              <button key={f} type="button" onClick={() => toggleChip(fins, setFins, f)} style={{background: fins.includes(f) ? "#111827" : "#F3F4F6", color: fins.includes(f) ? "#FACC15" : "#374151", border:"none",padding:"6px 12px",borderRadius:999,fontSize:11,fontWeight:600,cursor:"pointer"}}>{f}</button>
            ))}
          </div>
        </div>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Additional notes (optional)</label>
        <textarea data-testid="sp-notes" value={notes} onChange={e => setNotes(e.target.value.slice(0, 600))} rows={2} placeholder="Any brand/structure/finish context we should pass to the curators…" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 4px",fontFamily:"inherit"}}/>
        <span style={{fontSize:10,color:"#9CA3AF",display:"block",marginBottom:14}}>Contact details (email, phone, URLs) will be auto-removed.</span>

        <div style={{background:"#F9FAFB",padding:14,borderRadius:10,border:"1px solid #E5E7EB",marginBottom:14}}>
          <h4 style={{margin:"0 0 10px",fontSize:12,fontWeight:700,color:"#111827"}}>Shipping address</h4>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <input data-testid="sp-ship-name" placeholder="Full name" value={ship.ship_name} onChange={e => setShip({...ship, ship_name: e.target.value})} style={{padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
            <input data-testid="sp-ship-phone" placeholder="Phone" value={ship.ship_phone} onChange={e => setShip({...ship, ship_phone: e.target.value})} style={{padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
          </div>
          <input data-testid="sp-ship-a1" placeholder="Address line 1" value={ship.ship_address_line1} onChange={e => setShip({...ship, ship_address_line1: e.target.value})} style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,marginTop:8}}/>
          <input placeholder="Address line 2 (optional)" value={ship.ship_address_line2} onChange={e => setShip({...ship, ship_address_line2: e.target.value})} style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,marginTop:8}}/>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:8,marginTop:8}}>
            <input placeholder="City" value={ship.ship_city} onChange={e => setShip({...ship, ship_city: e.target.value})} style={{padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
            <input placeholder="State" value={ship.ship_state} onChange={e => setShip({...ship, ship_state: e.target.value})} style={{padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
            <input placeholder="Pincode" value={ship.ship_postal_code} onChange={e => setShip({...ship, ship_postal_code: e.target.value})} style={{padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13}}/>
          </div>
        </div>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginTop:10}}>
          <div style={{fontSize:12,color:"#6B7280"}}>No payment yet — <b style={{color:"#111827"}}>quote arrives within 24 hours</b>.</div>
          <div style={{display:"flex",gap:8}}>
            <button type="button" onClick={onClose} disabled={busy} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            <button data-testid="sp-submit" type="button" onClick={submit} disabled={busy} style={{background: busy ? "#9CA3AF" : "#111827", color:"#FACC15", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: busy ? "not-allowed" : "pointer"}}>{busy ? "Submitting…" : "Submit for quote"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


export function SamplePackRatingModal({ samplePack, onClose, onDone }) {
  const [r, setR] = useState({ stars: 5, print_quality: 5, material_feel: 5, packaging_care: 5, feedback: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const dims = [
    ["stars", "Overall rating"],
    ["print_quality", "Print quality"],
    ["material_feel", "Material feel"],
    ["packaging_care", "Packaging care & presentation"],
  ];

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/sample-packs/${samplePack.id}/rate`, r);
      toast.success(`Thanks for rating the sample pack ${r.stars}/5 ★`);
      onDone && onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save rating");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="sample-pack-rating-modal" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:560,maxWidth:"96vw",padding:"24px 28px",maxHeight:"92vh",overflow:"auto"}}>
        <h3 style={{margin:"0 0 6px",fontFamily:"'Fraunces', serif",fontSize:22}}>Rate your sample pack</h3>
        <p style={{margin:"0 0 16px",fontSize:12,color:"#6B7280"}}>Your rating helps us refine which printers end up in future kits.</p>
        {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}
        <div style={{display:"grid",gap:12,marginBottom:14}}>
          {dims.map(([k, label]) => (
            <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 12px",border:"1px solid #E5E7EB",borderRadius:8,background:"#F9FAFB"}}>
              <label style={{fontSize:12,fontWeight:600}}>{label}</label>
              <div style={{display:"flex",gap:4}}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button" onClick={() => setR({...r, [k]: s})} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:22,lineHeight:1,color: r[k] >= s ? "#F59E0B" : "#D1D5DB"}}>★</button>
                ))}
                <b style={{fontSize:11,marginLeft:6,minWidth:24}}>{r[k]}/5</b>
              </div>
            </div>
          ))}
        </div>
        <textarea data-testid="sp-rating-feedback" value={r.feedback} onChange={e => setR({...r, feedback: e.target.value.slice(0, 1200)})} rows={3} placeholder="Which printer stood out? Anything we should curate differently next time?" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,fontFamily:"inherit"}}/>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:14}}>
          <button type="button" onClick={onClose} disabled={busy} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Not now</button>
          <button data-testid="sp-rating-submit" type="button" onClick={submit} disabled={busy} style={{background: busy ? "#9CA3AF" : "#111827", color:"#FACC15", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: busy ? "not-allowed" : "pointer"}}>{busy ? "Saving…" : "Submit rating ★"}</button>
        </div>
      </div>
    </div>
  );
}
